import { supabase } from '../lib/supabase';

export const receiptService = {
  /**
   * Uploads receipt image to storage and creates receipt record.
   * @param {File} file - Receipt image file.
   * @param {Object} receiptData - Receipt metadata.
   * @returns {Promise<Object>} Created receipt record.
   */
  async createReceipt(file, receiptData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload image to storage
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      const { error: uploadError } = await supabase?.storage
        ?.from('receipts')
        ?.upload(filePath, file);

      if (uploadError) throw uploadError;

      // Create receipt record
      const { data, error } = await supabase?.from('receipts')?.insert({
        user_id: user?.id,
        merchant_name: receiptData?.merchantName,
        receipt_date: receiptData?.date,
        total_amount: receiptData?.totalAmount,
        tax_amount: receiptData?.taxAmount || null,
        image_path: filePath,
        ocr_confidence: receiptData?.overallConfidence || null,
        notes: receiptData?.notes || null,
        transaction_id: receiptData?.transactionId || null
      })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        transactionId: data?.transaction_id,
        merchantName: data?.merchant_name,
        receiptDate: data?.receipt_date,
        totalAmount: parseFloat(data?.total_amount),
        taxAmount: data?.tax_amount ? parseFloat(data?.tax_amount) : null,
        imagePath: data?.image_path,
        ocrConfidence: data?.ocr_confidence ? parseFloat(data?.ocr_confidence) : null,
        notes: data?.notes,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating receipt:', error);
      throw error;
    }
  },

  /**
   * Gets all receipts for the current user.
   * @returns {Promise<Array>} List of receipts.
   */
  async getAll() {
    try {
      const { data, error } = await supabase?.from('receipts')
        ?.select(`
          *,
          transaction:transactions!receipts_transaction_id_fkey(id, description, amount)
        `)
        ?.order('receipt_date', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        transactionId: row?.transaction_id,
        transaction: row?.transaction ? {
          id: row?.transaction?.id,
          description: row?.transaction?.description,
          amount: parseFloat(row?.transaction?.amount)
        } : null,
        merchantName: row?.merchant_name,
        receiptDate: row?.receipt_date,
        totalAmount: parseFloat(row?.total_amount),
        taxAmount: row?.tax_amount ? parseFloat(row?.tax_amount) : null,
        imagePath: row?.image_path,
        ocrConfidence: row?.ocr_confidence ? parseFloat(row?.ocr_confidence) : null,
        notes: row?.notes,
        createdAt: row?.created_at
      }));
    } catch (error) {
      console.error('Error fetching receipts:', error);
      throw error;
    }
  },

  /**
   * Gets a single receipt by ID.
   * @param {string} receiptId - Receipt ID.
   * @returns {Promise<Object>} Receipt record.
   */
  async getById(receiptId) {
    try {
      const { data, error } = await supabase?.from('receipts')
        ?.select(`
          *,
          transaction:transactions!receipts_transaction_id_fkey(id, description, amount, category:categories(name, color))
        `)
        ?.eq('id', receiptId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        transactionId: data?.transaction_id,
        transaction: data?.transaction ? {
          id: data?.transaction?.id,
          description: data?.transaction?.description,
          amount: parseFloat(data?.transaction?.amount),
          category: data?.transaction?.category
        } : null,
        merchantName: data?.merchant_name,
        receiptDate: data?.receipt_date,
        totalAmount: parseFloat(data?.total_amount),
        taxAmount: data?.tax_amount ? parseFloat(data?.tax_amount) : null,
        imagePath: data?.image_path,
        ocrConfidence: data?.ocr_confidence ? parseFloat(data?.ocr_confidence) : null,
        notes: data?.notes,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error fetching receipt:', error);
      throw error;
    }
  },

  /**
   * Gets public URL for receipt image.
   * @param {string} imagePath - Storage path of the image.
   * @returns {string} Public URL.
   */
  getReceiptImageUrl(imagePath) {
    const { data } = supabase?.storage?.from('receipts')?.getPublicUrl(imagePath);
    return data?.publicUrl;
  },

  /**
   * Deletes a receipt and its image.
   * @param {string} receiptId - Receipt ID.
   * @returns {Promise<void>}
   */
  async delete(receiptId) {
    try {
      // Get receipt to find image path
      const { data: receipt, error: fetchError } = await supabase?.from('receipts')
        ?.select('image_path')
        ?.eq('id', receiptId)
        ?.single();

      if (fetchError) throw fetchError;

      // Delete image from storage
      const { error: storageError } = await supabase?.storage
        ?.from('receipts')
        ?.remove([receipt?.image_path]);

      if (storageError) throw storageError;

      // Delete receipt record
      const { error: deleteError } = await supabase?.from('receipts')
        ?.delete()
        ?.eq('id', receiptId);

      if (deleteError) throw deleteError;
    } catch (error) {
      console.error('Error deleting receipt:', error);
      throw error;
    }
  },

  /**
   * Links a receipt to a transaction.
   * @param {string} receiptId - Receipt ID.
   * @param {string} transactionId - Transaction ID.
   * @returns {Promise<void>}
   */
  async linkToTransaction(receiptId, transactionId) {
    try {
      const { error } = await supabase?.from('receipts')
        ?.update({ transaction_id: transactionId })
        ?.eq('id', receiptId);

      if (error) throw error;

      // Also update transaction with receipt_id
      const { error: txError } = await supabase?.from('transactions')
        ?.update({ receipt_id: receiptId })
        ?.eq('id', transactionId);

      if (txError) throw txError;
    } catch (error) {
      console.error('Error linking receipt to transaction:', error);
      throw error;
    }
  }
};